import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Card, Statistic, Button, Typography, Alert, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ClockCircleOutlined, LockOutlined, ArrowRightOutlined, PercentageOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { getBalance } from '../../services/balance'
import { checkPayoutEligibility } from '../../services/payout'
import type { PayoutEligibilityResponse } from '../../services/payout'
import { getMerchant } from '../../services/merchant'
import { getSettlementConfig } from '../../services/config'
import type { AcquiringPaymentMethod, Balance as BalanceType, Merchant, MerchantAcquiringRate, PayoutCapability } from '../../types'

const { Title } = Typography

type SettlementRateRow = {
  key: string
  channel: string
  fee: string
  minimum: string
}

const paymentMethodLabels: Record<AcquiringPaymentMethod, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: "Touch'n Go eWallet",
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  PAYPAY: 'PayPay',
  MERPAY: 'Merpay',
  AUPAY: 'au PAY',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  KAKAO_PAY: 'Kakao Pay',
  NAVER_PAY: 'Naver Pay',
  TOSS_PAY: 'Toss Pay',
  PAYCO: 'PAYCO',
  SAMSUNG_PAY: 'Samsung Pay',
  T_MONEY: 'T-money',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
}

const PAYOUT_CAPABILITY_ROWS: Array<{ capability: PayoutCapability; channel: string }> = [
  { capability: 'OVERSEAS_BUSINESS_ACCOUNT', channel: '海外企業賬戶' },
  { capability: 'OVERSEAS_EWALLET_ORDINARY', channel: '海外電子錢包（普通提現）' },
  { capability: 'OVERSEAS_EWALLET_URGENT', channel: '海外電子錢包（緊急提現）' },
  { capability: 'PERSONAL_EWALLET', channel: '個人電子錢包' },
  { capability: 'PERSONAL_BANK', channel: '個人銀行賬戶' },
]

export default function Balance() {
  const [balance, setBalance] = useState<BalanceType | null>(null)
  const [eligibility, setEligibility] = useState<PayoutEligibilityResponse | null>(null)
  const [merchant, setMerchant] = useState<Merchant | null>(null)
  const [settlementDays, setSettlementDays] = useState<number>(1)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const handleCreatePayout = () => {
    navigate('/dashboard/payouts/new')
  }

  useEffect(() => {
    Promise.all([
      getBalance(),
      checkPayoutEligibility(),
      getMerchant(),
      getSettlementConfig()
    ]).then(([balanceRes, eligibilityRes, merchantRes, settlementRes]) => {
      setBalance(balanceRes.data)
      setEligibility(eligibilityRes.data)
      setMerchant(merchantRes.data)
      setSettlementDays(settlementRes.data.settlementDays)
    }).finally(() => setLoading(false))
  }, [])

  const formatRate = (rate: string) => {
    return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
  }

  const formatAmount = (amount: string | number) => Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const formatMoney = (amount: string | number) => `$${formatAmount(amount)}`
  const formatUsdFee = (amount: string | number) => `USD ${formatAmount(amount)}`
  const formatFee = (rate: string, fixed?: string | null) => {
    const rateAmount = Number(rate)
    const fixedAmount = fixed == null ? 0 : Number(fixed)
    const parts: string[] = []
    if (rateAmount > 0) parts.push(formatRate(rate))
    if (fixedAmount > 0) parts.push(formatUsdFee(fixedAmount))
    return parts.length > 0 ? parts.join(' + ') : formatMoney(0)
  }
  const formatDays = (days: number) => `${days} 天`

  const acquiringRateColumns: ColumnsType<MerchantAcquiringRate> = [
    {
      title: '收單渠道',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: AcquiringPaymentMethod) => paymentMethodLabels[method] || method,
    },
    {
      title: '費率/單筆費用 (固定費 USD)',
      dataIndex: 'feeRate',
      key: 'feeRate',
      render: (_feeRate: string, row) => <Tag color="blue">{formatFee(row.feeRate, row.perTransactionFee)}</Tag>,
    },
    {
      title: '拒付費率/單筆拒付手續費 (固定費 USD)',
      dataIndex: 'chargebackRate',
      key: 'chargebackRate',
      render: (_chargebackRate: string, row) => formatFee(row.chargebackRate, row.chargebackFixedFee),
    },
    {
      title: '循環保證金比例/循環保證金天數',
      dataIndex: 'rollingReserveRate',
      key: 'rollingReserveRate',
      render: (_rollingReserveRate: string, row) => `${formatRate(row.rollingReserveRate)} / ${formatDays(row.rollingReserveDays)}`,
    },
    {
      title: '結算週期',
      dataIndex: 'settlementDays',
      key: 'settlementDays',
      render: (settlementDays: number) => `T+${settlementDays}`,
    },
  ]

  const settlementRateRows: SettlementRateRow[] = eligibility
    ? PAYOUT_CAPABILITY_ROWS
        .filter(({ capability }) => eligibility.enabledCapabilities.includes(capability))
        .map(({ capability, channel }) => ({
          key: capability,
          channel,
          fee: formatFee(eligibility.feeConfigs[capability].feeRate, eligibility.feeConfigs[capability].fixedFee),
          minimum: eligibility.minPayoutAmounts[capability],
        }))
    : []

  const settlementRateColumns: ColumnsType<SettlementRateRow> = [
    {
      title: '結款渠道',
      dataIndex: 'channel',
      key: 'channel',
    },
    {
      title: '費率',
      dataIndex: 'fee',
      key: 'fee',
      render: (fee: string) => <Tag color="geekblue">{fee}</Tag>,
    },
    {
      title: '最低結款金額',
      dataIndex: 'minimum',
      key: 'minimum',
      render: (minimum: string) => `${formatMoney(minimum)} USD`,
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>餘額概覽</Title>
        <Button onClick={() => navigate('/dashboard/balance/transactions')}>
          交易明細 <ArrowRightOutlined />
        </Button>
      </div>

      {eligibility && !eligibility.eligible && (
        <Alert
          type="warning"
          message="結款提示"
          description={eligibility.reasons.join('；')}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} md={6}>
          <Card loading={loading}>
            <Statistic
              title="可用餘額"
              value={balance ? formatAmount(balance.available) : 0}
              prefix="$"
              suffix="USD"
              valueStyle={{ color: '#52c41a' }}
            />
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              disabled={!eligibility?.eligible}
              onClick={handleCreatePayout}
            >
              發起結款
            </Button>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={loading}>
            <Statistic
              title="待結算"
              value={balance ? formatAmount(balance.pending) : 0}
              prefix={<ClockCircleOutlined />}
              suffix="USD"
              valueStyle={{ color: '#faad14' }}
            />
            <p style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
              支付成功後資金將在 T+{settlementDays} 個工作日後結算到可用餘額
            </p>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={loading}>
            <Statistic
              title="凍結金額"
              value={balance ? formatAmount(balance.frozen) : 0}
              prefix={<LockOutlined />}
              suffix="USD"
              valueStyle={{ color: '#ff4d4f' }}
            />
            <p style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
              因拒付或風控被凍結的資金
            </p>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={loading}>
            <Statistic
              title="循環保證金"
              value={balance ? formatAmount(balance.rollingReserve) : 0}
              prefix={<SafetyCertificateOutlined />}
              suffix="USD"
              valueStyle={{ color: '#1677ff' }}
            />
            <p style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
              依收單渠道規則暫扣，達到釋放時間後轉入可用餘額
            </p>
          </Card>
        </Col>
      </Row>

      <Card
        loading={loading}
        style={{ marginTop: 24 }}
        title={
          <span>
            <PercentageOutlined style={{ marginRight: 8, color: '#1677ff' }} />
            費率說明
          </span>
        }
      >
        {merchant && (
          <>
            <Title level={5} style={{ marginTop: 0 }}>收單費率</Title>
            <Table
              size="small"
              pagination={false}
              columns={acquiringRateColumns}
              dataSource={merchant.acquiringRates}
              rowKey={(row) => row.paymentMethod}
              locale={{ emptyText: '暫未配置收單渠道' }}
            />

            <Title level={5} style={{ marginTop: 24 }}>結款費率</Title>
            <Table
              size="small"
              pagination={false}
              columns={settlementRateColumns}
              dataSource={settlementRateRows}
              locale={{ emptyText: '暫無結款費率資料' }}
            />

            <p style={{ marginTop: 12, marginBottom: 0, color: '#999', fontSize: 12 }}>
              單筆固定手續費和單筆拒付固定手續費以 USD 設定，不跟隨訂單貨幣；如需調整收單渠道或費率，請聯絡管理員。
            </p>
          </>
        )}
      </Card>
    </div>
  )
}
